import { useEffect, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { getUserProfile, setUserBlocked, subscribeBlockedUsers } from "~/lib/matrix";

/** Settings section listing blocked people with Unblock; absent when nobody is blocked. */
export function BlockedPeople() {
  const { client } = useMatrix();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    if (!client) return;
    return subscribeBlockedUsers(client, setUserIds);
  }, [client]);

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    for (const id of userIds) {
      if (names[id]) continue;
      void getUserProfile(client, id)
        .then((profile) => {
          if (cancelled || !profile.displayName) return;
          setNames((prev) => ({ ...prev, [id]: profile.displayName ?? id }));
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [client, userIds, names]);

  if (!client || userIds.length === 0) return null;

  const unblock = async (userId: string) => {
    setPending(userId);
    try {
      await setUserBlocked(client, userId, false);
    } catch (err) {
      console.warn("unblock failed", err);
    } finally {
      setPending(null);
    }
  };

  return (
    <section aria-label="Blocked people" className="flex flex-col gap-2">
      <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
        Blocked people
      </span>
      <ul className="flex flex-col">
        {userIds.map((id) => (
          <li key={id} className="flex h-11 items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm font-light text-foreground/90">
              {names[id] ?? id}
            </span>
            <button
              type="button"
              disabled={pending === id}
              onClick={() => void unblock(id)}
              aria-label={`Unblock ${names[id] ?? id}`}
              className="h-11 text-sm font-light text-muted-foreground outline-none hover:text-foreground/80 focus-visible:shadow-neon-cyan disabled:opacity-40"
            >
              Unblock
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
